import { useInfiniteList } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { FaceProfileResponse, VoiceProfileResponse } from "pages/media/types";
import { useCallback, useMemo } from "react";
import { useOrganization } from "hooks/useOrganization";
import {
  ActiveTabName,
  VOICE_ACCENT_FILTER_KEY,
  VOICE_LANGUAGE_FILTER_KEY,
  VOICE_LOCALE_FILTER_KEY,
} from "../types";
import { getNextPageParamWithServerPagination } from "utils/pagination/";

interface ProfileFilters {
  [key: string]: string[];
}
interface UseInfiniteVoiceFaceProfilesProps<T> {
  filters?: ProfileFilters;
  resourceType?: ActiveTabName;
  pageSize?: number;
  type?: "face" | "voice";
  previewProfile?: T;
  insertSelected?: boolean;
  enabled?: boolean;
}

export function useInfiniteVoiceFaceProfiles<
  T extends Partial<FaceProfileResponse> | VoiceProfileResponse,
>({
  type = "face",
  filters,
  previewProfile,
  resourceType = ActiveTabName.Premade,
  pageSize = 10,
  insertSelected = true,
  enabled = true,
}: UseInfiniteVoiceFaceProfilesProps<T> = {}) {
  const { organization } = useOrganization({});
  const queryClient = useQueryClient();
  let resource =
    resourceType === "Premade"
      ? `media/${type}_profiles/premade`
      : `media/${organization?.id}/${type}_profiles`;
  if (filters && Object.keys(filters).length > 0) {
    if (type === "voice" && resourceType === ActiveTabName.Premade) {
      const {
        [VOICE_LANGUAGE_FILTER_KEY]: languageSelections,
        [VOICE_ACCENT_FILTER_KEY]: accentSelections,
        [VOICE_LOCALE_FILTER_KEY]: localeSelections,
        ...tagFilters
      } = filters;
      const params = new URLSearchParams();
      if (Object.keys(tagFilters).length > 0) {
        params.set("tags", JSON.stringify(tagFilters));
      }
      const langCode = languageSelections?.[0];
      if (langCode) {
        const accent = accentSelections?.[0];
        const baseLangFilter: {
          language: string;
          accent?: string;
          locale?: string;
        } = { language: langCode };
        if (accent) baseLangFilter.accent = accent;
        const langFilters =
          localeSelections && localeSelections.length > 0
            ? localeSelections.map((locale) => ({ ...baseLangFilter, locale }))
            : [baseLangFilter];
        params.set("languages", JSON.stringify(langFilters));
      }
      const qs = params.toString();
      if (qs) {
        resource += `?${qs}`;
      }
    } else {
      resource += `?tags=${JSON.stringify(filters)}`;
    }
  }
  // The most important thing in this hook!
  const queryKey = [
    ...getQueryKey(type, resourceType, organization?.id ?? ""),
    pageSize,
    filters, // Note the filters.
  ];

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteList<T>({
    resource,
    pagination: {
      pageSize,
      mode: "server",
    },
    queryOptions: {
      staleTime: 1000 * 60 * 60, // one hour
      keepPreviousData: false,
      enabled: !!organization?.id && enabled,
      getNextPageParam: getNextPageParamWithServerPagination(pageSize),
      queryKey,
    },
  });

  const loadMoreData = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage().catch((error) => {
        console.error("Error fetching next page:", error);
      });
    }
  }, [fetchNextPage, hasNextPage]);

  // Extract all face profiles from paginated data and filter out the preview profile to avoid duplicates
  const allProfiles = useMemo(() => {
    // Get all profiles from paginated data
    const profiles =
      data?.pages.reduce<T[]>(
        (acc, page) => [
          ...acc,
          ...page.data.filter((profile) => profile.status !== "Disabled"),
        ],
        []
      ) || [];

    const selectedIndex = profiles.findIndex(
      (profile) => profile.id === previewProfile?.id
    );

    const insertedIsNotSame = profiles[0]?.id !== previewProfile?.id;
    if (insertSelected && previewProfile && insertedIsNotSame) {
      if (selectedIndex !== -1) {
        // remove from its regular place in page
        profiles.splice(selectedIndex, 1);
      }
      // Move the selected profile to the beginning of the list
      profiles.unshift(previewProfile);
    }

    return profiles;
  }, [data, previewProfile, insertSelected]);

  // Function to add a new face profile at the beginning of the list
  const addProfile = (newProfile: T) => {
    queryClient.setQueryData<typeof data>(queryKey, (oldData) => {
      if (!oldData?.pages.length) {
        return {
          pages: [{ data: [newProfile], total: 1 }],
          pageParams: [undefined],
        };
      }

      const newPages = [...oldData.pages];
      newPages[0] = {
        ...newPages[0],
        data: [newProfile, ...(newPages[0]?.data ?? [])],
        total: (newPages[0]?.total ?? 0) + 1,
      };

      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  return {
    profiles: allProfiles,
    isLoading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMoreData,
    addProfile,
    previewProfile,
    refetch,
  };
}

export const getQueryKey = (
  type: "face" | "voice",
  resourceType: ActiveTabName,
  organizationId: string
) => {
  return [`${type}ProfilesList`, resourceType, organizationId];
};
